import { Icon20InfoCircleOutline } from '@vkontakte/icons';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { dispatchEnableAutoRenewCommunication, dispatchEnableAutoRenewFromHomeBanner } from 'reactApp/hooks/subscriptionCommunication';
import { getAutoRenewalDisabledBannerTitle } from 'reactApp/hooks/subscriptionCommunication.helpers';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './AutoRenewalBanner.css';

const ANALYTICS_NAME = 'mnz_auto_renewal_banner';

const sendDwhEvent = (action: 'show' | 'click') => {
    sendDwh({
        eventCategory: 'banner',
        action,
        dwh_enabled: true,
        dwhData: {
            name: ANALYTICS_NAME,
            platform: IS_PHONE_BROWSER ? 'touch' : 'web',
            ...(action === 'click' ? { button_name: 'enable_auto_renewal' } : {}),
        },
    });
};

interface AutoRenewalBannerProps {
    subscription: BillingDataSubscriptionInfo;
    hasCard: boolean;
    isCardLoaded: boolean;
}

export const AutoRenewalBanner = memo(({ subscription, hasCard, isCardLoaded }: AutoRenewalBannerProps) => {
    const dispatch = useDispatch();
    const { goToSubscriptions } = useGoToSubscriptions();
    const { card } = useSelector(CardSelectors.getCard);
    const [shouldEnableAutoRenewal, setShouldEnableAutoRenewal] = useState(false);

    const openAddCardModal = useCallback(() => {
        if (!subscription) {
            return;
        }

        dispatchEnableAutoRenewCommunication(dispatch, { subscription, card: null });
        goToSubscriptions();
    }, [dispatch, goToSubscriptions, subscription]);

    const handleEnableAutoRenewal = useCallback(() => {
        sendDwhEvent('click');

        if (!subscription) {
            return;
        }

        if (hasCard && card) {
            dispatchEnableAutoRenewFromHomeBanner(dispatch, { subscription, card, goToSubscriptions });

            return;
        }

        if (!hasCard) {
            openAddCardModal();

            return;
        }

        if (!card) {
            setShouldEnableAutoRenewal(true);
            dispatch(CardActions.loadCard());

            return;
        }
    }, [hasCard, subscription, card, dispatch, openAddCardModal, goToSubscriptions]);

    useEffect(() => {
        sendDwhEvent('show');
    }, []);

    useEffect(() => {
        if (!shouldEnableAutoRenewal || !isCardLoaded) {
            return;
        }

        if (card) {
            setShouldEnableAutoRenewal(false);
            dispatchEnableAutoRenewFromHomeBanner(dispatch, { subscription, card, goToSubscriptions });

            return;
        }

        setShouldEnableAutoRenewal(false);
        openAddCardModal();
    }, [card, dispatch, goToSubscriptions, isCardLoaded, openAddCardModal, shouldEnableAutoRenewal, subscription]);

    const title = getAutoRenewalDisabledBannerTitle(subscription);
    const subtitle = 'Файлам может не хватить места. Если так случится, в Почте и Облаке появятся ограничения';

    return (
        <div className={styles.root}>
            <Icon20InfoCircleOutline className={styles.icon} />
            <div className={styles.content}>
                <div className={styles.title}>{title}</div>
                <div className={styles.text}>{subtitle}</div>
            </div>
            <div className={styles.buttons}>
                <Button
                    className={styles.button}
                    data-name="primary-bttn"
                    data-qa-id="primary-bttn"
                    theme="octavius"
                    sizeMode={ButtonSizeMode.small}
                    onClick={handleEnableAutoRenewal}
                >
                    Включить автоплатёж
                </Button>
            </div>
        </div>
    );
});

AutoRenewalBanner.displayName = 'AutoRenewalBanner';
